import { useEffect } from "react";

import {
  ATTENTION_WELL_DOT_GLOW_LAYER,
  ATTENTION_WELL_DOT_LAYER,
  ATTENTION_WELL_JUMP_GLOW_LAYER,
  ATTENTION_WELL_JUMP_LAYER,
  BASE_WELL_LAYER,
  BASE_WELL_RASTER_LAYER,
  FACILITY_LABEL_LAYER,
  FACILITY_LAYER,
  GRID_LABEL_DEFAULT_SIZES,
  HZ_WELL_RASTER_LAYER,
  IPDB_CONTROL_WELLS_LAYER,
  IPDB_LAYER,
  SECTION_LABEL_LAYER,
  SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
  SELECTED_SINGLE_WELL_POINT_LAYER,
  STICK_LAYER,
  TYPE_LOG_LAYER,
  TYPE_LOG_VERTICAL_LAYER,
  WELL_HIGHLIGHT_LAYERS,
  WELL_LABEL_LAYER,
  WELL_LAYER,
  WELL_LAYER_HIGHLIGHTED,
  WELL_LAYER_POINT,
  WELL_LAYER_POINT_HIGHLIGHTED,
  XDA_PROJECTION_LAYER,
  highlightedWellLineThickness
} from "constants/mapLayers.constants";
import { selectDisplaySettings, selectLayers, useMapStore } from "stores/map";

import { shapefileLayerType } from "components/project/shapefiles/shapefiles.constants";

import { setWellLineWidth, wellDotSizePaint } from "../../utils/baseWellGlyphs";
import { getLayerZoomParent } from "../update-map-layers-visiblity/useUpdateMapLayersVisibility";
import {
  applyCollisionToLayer,
  getCollisionProperties,
  getDensityKeyForLayer
} from "../useLabelDensity";
import { useMapContext } from "../useMapContext";

interface IUseManageMapboxLayerStylesParams {
  mapbox: mapboxgl.Map;
}

/**
 * Layers that manage their own visibility via dedicated hooks/components.
 * These are skipped to prevent this hook from overriding their toggle state.
 */
export const SELF_MANAGED_LAYERS = new Set<string>([
  IPDB_LAYER,
  IPDB_CONTROL_WELLS_LAYER,
  XDA_PROJECTION_LAYER,
  ...WELL_HIGHLIGHT_LAYERS
]);

/**
 * Layers whose zoom range this hook must NOT touch — a second writer that
 * unconditionally calls setLayerZoomRange, with no arbiter between it and
 * the layer's real owner, is last-writer-wins (see the inline comment below,
 * which documents this for STICK_LAYER, EVA-6899).
 *
 * The three base-well layers (EVA-35) are the same hazard: MapboxWrapper's
 * own base-well effect is the crossover-aware owner of their zoom range — it
 * narrows the MVT pair (`BASE_WELL_LAYER`/`${BASE_WELL_LAYER}-lines`) and the
 * raster layer to opposite sides of HZ_RASTER_MVT_CROSSOVER_ZOOM, re-deriving
 * that split from `displaySettings.layerZoomSettings[BASE_WELL_LAYER]` on
 * every change (default OR user-narrowed). This hook fires on unrelated
 * signals too (any displaySettings change, any mapboxLayers identity change),
 * and would otherwise reset all three straight back to whatever getMapLayers
 * declared them with (0-22) or, if a custom range IS set, to that RAW range
 * with no crossover split at all — vector tiles capped at 16k wells drawing
 * at continental zoom, exactly the bug the raster crossover exists to avoid.
 *
 * WELL_LAYER and WELL_LAYER_POINT (EVA-237) are exempt for the identical
 * reason: the raster/MVT crossover in useMapTileSourceManager
 * (applyWellLayerZoomRanges) is now their sole owner, splitting the user's
 * range across HZ_RASTER_MVT_CROSSOVER_ZOOM the same way. Before this fix,
 * this hook applied the user's RAW range to these two layers while the
 * crossover applied its own split range with no arbiter between the two —
 * last-writer-wins after a style reload let wells render outside the
 * configured zoom range.
 *
 * TYPE_LOG_LAYER and TYPE_LOG_VERTICAL_LAYER are owned by useTypeLogWellsLayer,
 * which floors the user's range by well count so the hz/vert tiles never load
 * below the crossover on maps too large for it.
 */
const ZOOM_RANGE_EXEMPT_LAYERS = new Set([
  // The jump highlight and the card's dot mark one well and show at every zoom,
  // whatever range the wells they ride on are given. The card's line keeps its
  // Selected Well range, and its glow follows it as a dependant layer.
  ATTENTION_WELL_JUMP_GLOW_LAYER,
  ATTENTION_WELL_JUMP_LAYER,
  ATTENTION_WELL_DOT_GLOW_LAYER,
  ATTENTION_WELL_DOT_LAYER,
  SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
  SELECTED_SINGLE_WELL_POINT_LAYER,
  STICK_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT,
  BASE_WELL_LAYER,
  `${BASE_WELL_LAYER}-lines`,
  BASE_WELL_RASTER_LAYER,
  TYPE_LOG_LAYER,
  TYPE_LOG_VERTICAL_LAYER
]);

function applyWellStyles(mapbox: mapboxgl.Map, layerId: string, displaySettings) {
  if (displaySettings.wellLineThickness > 0) {
    setWellLineWidth(mapbox, layerId, displaySettings.wellLineThickness);
    if (layerId === WELL_LAYER && mapbox.getLayer(WELL_LAYER_HIGHLIGHTED)) {
      setWellLineWidth(
        mapbox,
        WELL_LAYER_HIGHLIGHTED,
        highlightedWellLineThickness(displaySettings.wellLineThickness)
      );
    }
  }

  if (displaySettings.wellDotThickness > 0) {
    for (const id of [WELL_LAYER_POINT, WELL_LAYER_POINT_HIGHLIGHTED]) {
      if (mapbox.getLayer(id)) {
        for (const [prop, value] of Object.entries(
          wellDotSizePaint(displaySettings.wellDotThickness)
        )) {
          mapbox.setPaintProperty(id, prop, value);
        }
      }
    }
  }
}

function applyFacilityStyles(mapbox: mapboxgl.Map, displaySettings) {
  if (displaySettings.facilityIconSize > 0) {
    const iconSize = [
      "interpolate",
      ["linear"],
      ["zoom"],
      6,
      0.2 * displaySettings.facilityIconSize,
      10,
      1.0 * displaySettings.facilityIconSize,
      12,
      1.5 * displaySettings.facilityIconSize,
      22,
      2.0 * displaySettings.facilityIconSize
    ];
    mapbox.setLayoutProperty(FACILITY_LAYER, "icon-size", iconSize);
  }
}

function applyLabelSize(mapbox: mapboxgl.Map, layerId: string, displaySettings) {
  if (layerId === WELL_LABEL_LAYER) {
    mapbox.setLayoutProperty(layerId, "text-size", displaySettings.wellLabelSize);
  } else if (layerId === FACILITY_LABEL_LAYER) {
    mapbox.setLayoutProperty(layerId, "text-size", displaySettings.facilityLabelSize);
  } else if (layerId?.includes(shapefileLayerType.label)) {
    mapbox.setLayoutProperty(
      layerId,
      "text-size",
      displaySettings.shapefileNameLabelSize
    );
  } else if (layerId?.includes(shapefileLayerType.property)) {
    mapbox.setLayoutProperty(
      layerId,
      "text-size",
      displaySettings.shapefileDetailsLabelSize
    );
  } else if (layerId === SECTION_LABEL_LAYER) {
    // Canadian section labels only — the US and Texas grid labels are sized
    // per layer below.
    mapbox.setLayoutProperty(layerId, "text-size", displaySettings.sectionLabelSize);
  } else if (GRID_LABEL_DEFAULT_SIZES[layerId] !== undefined) {
    mapbox.setLayoutProperty(
      layerId,
      "text-size",
      displaySettings.gridLabelSizes?.[layerId] ?? GRID_LABEL_DEFAULT_SIZES[layerId]
    );
  }
}

/**
 * Visual changes to the mapbox layers are applied here based on the redux map layers. (visibility, zoom, etc)
 *
 * Example use cases:
 * Primarily to update properties on mapbox that aren't stored in the backend, the shapefiles are for the most part updated in the shapefile loader.
 *
 * Visibility is updated on layer tree/list check handler, and in project initialization (useProjectInitialization hook).
 * Both cases utilize the useUpdateMapLayersVisibility hook to set the redux map layers.
 *
 * Zoom is handled with the layer tree/list ZoomSlider component
 * @param param0
 */
export function useManageMapboxLayerStyles({
  mapbox
}: IUseManageMapboxLayerStylesParams) {
  const mapboxLayers = useMapStore(selectLayers);
  const displaySettings = useMapStore(selectDisplaySettings);

  const { isStyleLoaded } = useMapContext();

  useEffect(() => {
    if (!mapbox || !isStyleLoaded) {
      return;
    }

    for (const layer of mapboxLayers) {
      if (SELF_MANAGED_LAYERS.has(layer.id)) {
        continue;
      }

      if (!mapbox.getLayer(layer.id)) {
        continue;
      }

      // Visibility
      mapbox.setLayoutProperty(
        layer.id,
        "visibility",
        layer.isVisible ? "visible" : "none"
      );

      // Zoom range.
      // STICK_LAYER is exempt (EVA-6899): its zoom range belongs solely to the
      // raster/MVT crossover in useMapTileSourceManager, which gates it off below
      // HZ_RASTER_MVT_CROSSOVER_ZOOM so the shared hz.mvt source stops being
      // fetched there. A second writer would be last-writer-wins with no arbiter —
      // this effect is keyed [mapbox, mapboxLayers, isStyleLoaded, displaySettings]
      // while the crossover writes from updateWellAndFacilityTileUri, reached from
      // the updateMap effect gated on txnId/tile-URI change, so the two fire on
      // unrelated signals and neither ordering is guaranteed. Nothing populates
      // layerZoomSettings["Sticks"] today, but the sorter's pin block names
      // "add Sticks to the editable layer list" as its future alternative, and that
      // alone would hand ZoomSlider a pen on this layer.
      // The three base-well layers (EVA-35) are exempt for the identical reason —
      // see ZOOM_RANGE_EXEMPT_LAYERS above.
      // Guarded inline rather than through SELF_MANAGED_LAYERS: that set `continue`s
      // the whole iteration, which would also skip applyWellStyles below and let
      // stick line-width drift from displaySettings.wellLineThickness.
      // Dependent layers (EVA-237, DEPENDENT_LAYERS in useUpdateMapLayersVisibility)
      // have no ZoomSlider row of their own, so they fall back to the parent
      // row's setting when they have none of their own.
      const parent = getLayerZoomParent(layer.id);
      const zoomSettings =
        displaySettings.layerZoomSettings?.[layer.id] ??
        (parent && displaySettings.layerZoomSettings?.[parent]);
      const minzoom = zoomSettings?.minzoom ?? layer.minzoom;
      const maxzoom = zoomSettings?.maxzoom ?? layer.maxzoom;
      if (
        !ZOOM_RANGE_EXEMPT_LAYERS.has(layer.id) &&
        (minzoom !== undefined || maxzoom !== undefined)
      ) {
        mapbox.setLayerZoomRange(layer.id, minzoom, maxzoom);
      }

      // Layer-specific styles
      if (layer.id === WELL_LAYER || layer.id === STICK_LAYER) {
        // Sticks share WELL_LAYER's paint at creation (EVA-6899) but that's a
        // one-time snapshot — apply live display-setting changes here too so
        // sticks don't visually desync from wells after the initial render.
        applyWellStyles(mapbox, layer.id, displaySettings);
      }

      if (layer.id === FACILITY_LAYER) {
        applyFacilityStyles(mapbox, displaySettings);
      }

      applyLabelSize(mapbox, layer.id, displaySettings);

      // Apply collision properties for shapefile label layers synchronously
      // (avoids 300ms debounce delay in useLabelDensity for visual responsiveness)
      if (
        layer.id?.includes(shapefileLayerType.label) ||
        layer.id?.includes(shapefileLayerType.property)
      ) {
        const densityKey = getDensityKeyForLayer(layer.id);
        const density = displaySettings.labelDensity?.[densityKey] ?? 0;
        const collisionProps = getCollisionProperties(density);
        applyCollisionToLayer(mapbox, layer.id, collisionProps, density);
      }
    }

    // Sync raster well layer visibility with the Wells layer.
    // The raster layer is dynamically added and not part of the layer store,
    // so it must be explicitly kept in sync with the Wells layer's visibility.
    if (mapbox.getLayer(HZ_WELL_RASTER_LAYER)) {
      const wellLayer = mapboxLayers.find((l) => l.id === WELL_LAYER);
      if (wellLayer) {
        mapbox.setLayoutProperty(
          HZ_WELL_RASTER_LAYER,
          "visibility",
          wellLayer.isVisible ? "visible" : "none"
        );
      }
    }

    // Sync the sticks layer visibility with the Wells layer (EVA-6899).
    // Sticks are proposed-lateral wells on their own layer. STICK_LAYER *is* in
    // the layer store — Map.tsx's addLayer pushes every added layer into it — but
    // updateExistingLayersVisibility lists it in neither `checkedLayerKeys` nor
    // `DEPENDENT_LAYERS.well`, so its entry stays hidden and the loop above turns
    // it off. Like the raster layer, it must mirror the Wells layer's visibility,
    // otherwise sticks never render even though the source, filter, and counts are
    // all correct.
    if (mapbox.getLayer(STICK_LAYER)) {
      const wellLayer = mapboxLayers.find((l) => l.id === WELL_LAYER);
      if (wellLayer) {
        mapbox.setLayoutProperty(
          STICK_LAYER,
          "visibility",
          wellLayer.isVisible ? "visible" : "none"
        );
      }
    }
  }, [mapbox, mapboxLayers, isStyleLoaded, displaySettings]);
}
